import type { ColumnsType, ColumnType } from "@similarweb/pro-logic-components";

import { objectEntries } from "app/helpers/objects";
import type {
    ColumnSelectionOptions,
    SearchableSelectionOption,
    TableColumnType,
} from "app/pages/sales-intelligence/common-components/column-picker";
import type {
    ColummListColumn,
    ColumnFactoryList,
} from "app/pages/sales-intelligence/common-components/search-results-table";
import {
    createColumnCategoriesConfig,
} from "app/pages/sales-intelligence/modules/leads-search/configuration/columns/categories";
import type {
    LeadsColumnOptionsByLeadsType,
    LeadsColumnsConfigDto,
    LeadsSearchColumnListConfig,
    LeadsSearchRow,
    LeadsType,
} from "app/pages/sales-intelligence/modules/leads-search/types";
import type { TableColumnTypeDto } from "app/pages/sales-intelligence/modules/leads-search/types/base";
import type { IColumnSelectionResult } from "app/pages/sales-intelligence/modules/leads-search/types/columns";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

// `title` may be a rendered node (e.g. a header carrying a badge), which the picker cannot search on.
// `columnPickerTitle` is the declared plain-text form; the DTO name is the last resort so a column that
// omits it stays searchable rather than silently dropping out of the picker.
const resolveColumnPickerLabel = <RecordType, Column extends TableColumnType<string>>(
    column: Column,
    columnConfig: Readonly<ColummListColumn<RecordType>>,
): string => columnConfig.columnPickerTitle ?? (is.string(columnConfig.title) ? columnConfig.title : column.name);

export const createColumnOption = <
    RecordType,
    Column extends TableColumnType<Category>,
    Category extends string = string,
>(
    column: Column,
    columnConfig: Readonly<ColummListColumn<RecordType>>,
): SearchableSelectionOption<Category> => {
    const label = resolveColumnPickerLabel(column, columnConfig);

    return {
        ...column,
        label,
        additionalLabel: columnConfig.additionalLabel,
    };
};

export const fromDtoToColumnOptions = <
    RecordType,
    Dto extends TableColumnType<Category>,
    Category extends string = string,
    Ctx = void,
>(
    columnsDto: readonly Dto[],
    columnConfig: ColumnFactoryList<RecordType, unknown, unknown, Ctx>,
): ColumnSelectionOptions<Category> =>
    columnsDto.flatMap((col) => {
        const factory = columnConfig.get(col.id) ?? columnConfig.get(col.type);

        if (!factory) {
            return [];
        }

        const result = factory(col);
        const configs = is.readonlyArray(result) ? result : [result];

        return configs.map((config) => createColumnOption(col, config));
    });

export const mergeColumnOptionsWithMissingIndustryCodeColumns = (
    availableColumns: ColumnSelectionOptions,
    columnConfig: LeadsSearchColumnListConfig,
): ColumnSelectionOptions => {
    const existingIds = new Set(availableColumns.map((col) => col.id));
    const codesColumns: Array<keyof LeadsSearchRow> = ["sic_codes", "naics_codes"];

    const stubs: TableColumnTypeDto[] = codesColumns.flatMap((id) => {
        if (existingIds.has(id) || !columnConfig.has(id)) {
            return [];
        }

        return [
            {
                id,
                type: "regular",
                category: "companyAndWebsite",
                name: id,
                isRequired: false,
                isStatic: false,
            } satisfies TableColumnTypeDto,
        ];
    });

    if (stubs.length === 0) {
        return availableColumns;
    }

    return [...availableColumns, ...fromDtoToColumnOptions(stubs, columnConfig)];
};

export const fromDtoToColumnsConfig = <
    RecordType,
    Ctx = void,
    Category extends string = string,
    Dto extends TableColumnType<Category> = TableColumnType<Category>,
>(
    columnsDto: readonly Dto[],
    columnConfig: ColumnFactoryList<RecordType, unknown, unknown, Ctx>,
    ctx?: Ctx,
): ColumnsType<RecordType> => {
    return columnsDto.reduce<ColumnsType<RecordType>>((cols, col) => {
        const columnConfigFactory = columnConfig.get(col.id) ?? columnConfig.get(col.type);

        if (columnConfigFactory) {
            const config = columnConfigFactory(col, ctx);

            if (is.readonlyArray<ColumnType<RecordType>>(config)) {
                config.forEach((c) => cols.push(c));
            } else {
                cols.push(config as ColumnType<RecordType>);
            }
        }

        return cols;
    }, []);
};

export const fromLeadsConfigDtoToColumnOptions = (
    dto: LeadsColumnsConfigDto,
    columnConfig: LeadsSearchColumnListConfig,
): LeadsColumnOptionsByLeadsType => {
    const {
        categories,
        available: { common, ...byLeadType },
    } = dto;

    const { initialCategories, getCategoryConfig } = createColumnCategoriesConfig();

    const categoryConfigs = initialCategories.concat(categories).map(getCategoryConfig);

    const commonOptions = fromDtoToColumnOptions(common, columnConfig);

    return objectEntries(byLeadType).reduce<LeadsColumnOptionsByLeadsType>(
        (acc, [leadsType, specificDtoColumns]) => ({
            ...acc,
            available: {
                ...acc.available,
                [leadsType]: fromDtoToColumnOptions(specificDtoColumns, columnConfig).concat(acc.available[leadsType]),
            },
        }),
        {
            categories: categoryConfigs,
            available: {
                websites: commonOptions,
                companies: commonOptions,
            },
        },
    );
};

export const computeColumnSelectionOptions = <T extends { columns?: readonly TableColumnType[] }>(
    config: LeadsColumnOptionsByLeadsType,
    selected: T | undefined,
    columnConfig: LeadsSearchColumnListConfig,
    activeSearchLeadsType: LeadsType,
): IColumnSelectionResult => {
    const selectedColumns = fromDtoToColumnOptions(selected?.columns ?? [], columnConfig);
    const availableColumns = config.available[activeSearchLeadsType] ?? [];
    const availableColumnsSet = new Set(availableColumns.map((col) => col.id));

    // Pure functional approach - no side effects
    const filteredColumns = selectedColumns.filter((col) => col.type !== "utility");
    const selectedColumnIds = filteredColumns.map((col) => col.id);
    const additionalColumns = filteredColumns.filter((col) => !availableColumnsSet.has(col.id));

    return {
        selectedColumnIds,
        categories: config.categories,
        availableColumns: [...availableColumns, ...additionalColumns],
    };
};
